import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import {
  MapPin, Phone, Truck, ArrowLeft,
  Navigation, Clock, Package, AlertCircle, CheckCircle
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// LOADER LEAFLET
// Leaflet tidak bisa di-import biasa karena butuh window/DOM.
// Kita load via <script> tag, satu kali saja (singleton promise).
// ─────────────────────────────────────────────────────────────
let _leafletPromise = null;

function loadLeaflet() {
  // Sudah pernah dipanggil → kembalikan promise yang sama
  if (_leafletPromise) return _leafletPromise;

  _leafletPromise = new Promise((resolve, reject) => {
    // Leaflet sudah ada di window (misal: hot reload)
    if (window.L) { resolve(window.L); return; }

    // Inject CSS Leaflet
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id   = 'leaflet-css';
      link.rel  = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Inject JS Leaflet
    const script  = document.createElement('script');
    script.src    = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => resolve(window.L);
    script.onerror = () => {
      _leafletPromise = null; // reset agar bisa dicoba ulang
      reject(new Error('Gagal load Leaflet'));
    };
    document.head.appendChild(script);
  });

  return _leafletPromise;
}

// ─────────────────────────────────────────────────────────────
// KOMPONEN PETA
// Dipisah agar lifecycle React bersih.
// Props:
//   courierName  : string
//   courierPhone : string
//   location     : { lat, lng, accuracy, timestamp } | null
// ─────────────────────────────────────────────────────────────
function LiveMap({ courierName, courierPhone, location }) {
  const divRef  = useRef(null); // <div> yang akan dikuasai Leaflet
  const mapRef  = useRef(null); // instance L.Map
  const markRef = useRef(null); // instance L.Marker

  // ── INIT PETA — hanya sekali saat mount ──────────────────
  useEffect(() => {
    let cancelled = false;

    loadLeaflet().then((L) => {
      // Komponen sudah unmount atau div belum ada → batalkan
      if (cancelled || !divRef.current) return;
      // Sudah ada map (strict mode) → skip
      if (mapRef.current) return;

      // Pusat awal: Surabaya
      const map = L.map(divRef.current, {
        center: [-7.2575, 112.7521],
        zoom: 13,
      });

      // Tile OSM — gratis, tanpa API key
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      // Icon kurir pakai divIcon (tidak butuh file gambar)
      const icon = L.divIcon({
        className: '',  // kosong agar tidak ada class default Leaflet
        html: `
          <div style="
            width:46px; height:46px;
            background:linear-gradient(135deg,#15803d,#22c55e);
            border:3px solid white;
            border-radius:50%;
            box-shadow:0 3px 12px rgba(0,0,0,0.4);
            display:flex; align-items:center; justify-content:center;
            font-size:22px;
          ">🚚</div>
          <div style="
            width:0; height:0;
            border-left:7px solid transparent;
            border-right:7px solid transparent;
            border-top:9px solid #15803d;
            margin:0 auto;
          "></div>
        `,
        iconSize:    [46, 55],
        iconAnchor:  [23, 55],  // titik paling bawah = lokasi GPS
        popupAnchor: [0,  -58],
      });

      const marker = L.marker([-7.2575, 112.7521], { icon }).addTo(map);
      marker.bindPopup(`<b>${courierName || 'Kurir'}</b><br>Menunggu GPS...`);

      // Simpan ke ref agar bisa diakses dari useEffect lain
      mapRef.current  = map;
      markRef.current = marker;
    });

    // Cleanup: hancurkan map saat komponen unmount
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current  = null;
        markRef.current = null;
      }
    };
  }, []); // [] = jalankan sekali

  // ── UPDATE POSISI — setiap kali `location` berubah ───────
  // Ini yang membuat marker bergerak real-time tanpa reload peta
  useEffect(() => {
    if (!mapRef.current || !markRef.current || !location) return;

    const { lat, lng, accuracy, timestamp } = location;
    const latlng = [lat, lng];

    // Pindahkan marker ke koordinat baru
    markRef.current.setLatLng(latlng);

    // Update isi popup
    const jam = timestamp
      ? new Date(timestamp).toLocaleTimeString('id-ID')
      : '-';
    markRef.current.setPopupContent(
      `<b>${courierName}</b><br>` +
      `📱 ${courierPhone || '-'}<br>` +
      `🕐 ${jam}`
    );

    // Pan peta ke posisi kurir
    const zoom = mapRef.current.getZoom();
    mapRef.current.setView(latlng, zoom < 15 ? 16 : zoom);

  }, [location]); // re-run setiap location berubah

  // Div ini yang akan "diisi" Leaflet
  // WAJIB punya height eksplisit — tanpa ini peta tidak tampil
  return (
    <div
      ref={divRef}
      style={{ width: '100%', height: 400 }}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// HALAMAN TRACKING (publik — tanpa login)
// ─────────────────────────────────────────────────────────────
export default function CourierTracking() {
  const { courierId } = useParams();

  const [courier,    setCourier]    = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading,    setLoading]    = useState(true);

  // Ticker 15 detik → label "X detik lalu" ikut berubah
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  // Real-time: dokumen kurir di Firestore
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'couriers', courierId),
      (snap) => {
        if (snap.exists()) setCourier({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      () => setLoading(false) // error handler
    );
    return unsub; // hentikan listener saat unmount
  }, [courierId]);

  // Real-time: daftar pengantaran milik kurir ini
  useEffect(() => {
    const q = query(
      collection(db, 'deliveries'),
      where('courierId', '==', courierId)
    );
    return onSnapshot(q, (snap) =>
      setDeliveries(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [courierId]);

  // Hitung "berapa lama sejak update terakhir"
  const timeAgo = useCallback((ts) => {
    if (!ts) return '-';
    const secs = Math.round((Date.now() - new Date(ts)) / 1000);
    if (secs < 60)   return `${secs} detik lalu`;
    if (secs < 3600) return `${Math.floor(secs / 60)} menit lalu`;
    return `${Math.floor(secs / 3600)} jam lalu`;
  }, []);

  if (loading) return (
    <div style={{ paddingTop: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', color: '#64748b' }}>
        <Truck size={36} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.3 }} />
        Memuat data kurir...
      </div>
    </div>
  );

  if (!courier) return (
    <div style={{ paddingTop: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '80px 1rem' }}>
      <div className="card" style={{ textAlign: 'center', padding: '3rem', maxWidth: 360 }}>
        <AlertCircle size={36} style={{ display: 'block', margin: '0 auto 10px', color: '#dc2626' }} />
        <h2 style={{ marginBottom: 8 }}>Kurir Tidak Ditemukan</h2>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>Link tracking tidak valid.</p>
        <Link to="/monitoring" className="btn btn-primary">← Kembali ke Monitoring</Link>
      </div>
    </div>
  );

  const pending  = deliveries.filter(d => d.status !== 'selesai');
  const done     = deliveries.filter(d => d.status === 'selesai');
  const isActive = courier.status === 'active';
  const hasLoc   = !!courier.location?.lat;

  return (
    <div style={{ paddingTop: 64, minHeight: '100vh', background: '#f4fbf6' }}>

      {/* Header */}
      <div style={{ background: '#14532d', color: '#fff', padding: '0.875rem 1.25rem' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/monitoring" style={{ color: '#86efac', textDecoration: 'none', fontSize: 13, display: 'flex', alignItems: 'center', gap: 3, flex: 'none' }}>
            <ArrowLeft size={13} /> Monitoring
          </Link>
          <span style={{ color: '#5a9a6e' }}>›</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flex: 'none' }}>
              {courier.name?.charAt(0)?.toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{courier.name}</div>
              <div style={{ fontSize: 11, color: '#86efac' }}>{courier.vehicle || 'Motor'} · {courier.plateNumber || '-'}</div>
            </div>
          </div>
          {courier.phone && (
            <a href={`tel:${courier.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '5px 10px', color: '#fff', textDecoration: 'none', fontSize: 13, flex: 'none' }}>
              <Phone size={12} /> {courier.phone}
            </a>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, background: isActive ? 'rgba(22,163,74,0.25)' : 'rgba(148,163,184,0.15)', border: `1px solid ${isActive ? '#86efac' : 'rgba(255,255,255,0.1)'}`, flex: 'none' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#4ade80' : '#94a3b8', display: 'inline-block', animation: isActive ? 'blink 1.5s infinite' : 'none' }} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>{isActive ? 'Sedang Bertugas' : 'Tidak Aktif'}</span>
          </div>
        </div>
      </div>

      {/* Konten */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '1.25rem 1rem 3rem', display: 'grid', gridTemplateColumns: '1fr 300px', gap: '1.25rem', alignItems: 'start' }}>

        {/* Peta */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2f0e8', overflow: 'hidden' }}>
          {/* Status bar */}
          <div style={{ padding: '9px 14px', borderBottom: '1px solid #e2f0e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Navigation size={13} style={{ color: '#16a34a' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#14532d' }}>Lokasi Live Kurir</span>
              {isActive && hasLoc && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'blink 1.5s infinite' }} />}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Clock size={10} />
              {hasLoc ? `Diperbarui ${timeAgo(courier.location.timestamp)}` : 'GPS belum aktif'}
            </div>
          </div>

          {/* Wrapper peta + overlay */}
          <div style={{ position: 'relative' }}>
            <LiveMap
              courierName={courier.name}
              courierPhone={courier.phone}
              location={hasLoc ? courier.location : null}
            />
            {/* Overlay jika GPS belum aktif — di atas peta */}
            {!hasLoc && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(248,250,252,0.85)', backdropFilter: 'blur(3px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, zIndex: 500 }}>
                <MapPin size={32} style={{ color: '#94a3b8' }} />
                <div style={{ fontWeight: 600, color: '#475569' }}>GPS Belum Aktif</div>
                <div style={{ fontSize: 13, color: '#94a3b8' }}>Kurir belum menyalakan berbagi lokasi</div>
              </div>
            )}
          </div>

          {/* Koordinat */}
          {hasLoc && (
            <div style={{ padding: '8px 14px', background: '#f0fdf4', borderTop: '1px solid #bbf7d0', fontSize: 12, color: '#166534', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <span>📍 {courier.location.lat.toFixed(5)}, {courier.location.lng.toFixed(5)}</span>
              <span>📡 ~{Math.round(courier.location.accuracy || 0)}m</span>
              <a href={`https://maps.google.com/?q=${courier.location.lat},${courier.location.lng}`} target="_blank" rel="noreferrer" style={{ color: '#0369a1', textDecoration: 'none', marginLeft: 'auto' }}>
                Buka Google Maps ↗
              </a>
            </div>
          )}
        </div>

        {/* Daftar pengantaran */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="card" style={{ padding: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: deliveries.length > 0 ? 10 : 0 }}>
              {[
                { label: 'Total',   value: deliveries.length, color: '#166534' },
                { label: 'Proses',  value: pending.length,    color: '#d97706' },
                { label: 'Selesai', value: done.length,       color: '#16a34a' },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: 'center', background: '#f0fdf4', borderRadius: 10, padding: '8px 4px' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{s.label}</div>
                </div>
              ))}
            </div>
            {deliveries.length > 0 && (
              <>
                <div style={{ background: '#dcfce7', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round(done.length / deliveries.length * 100)}%`, background: 'linear-gradient(90deg,#16a34a,#22c55e)', borderRadius: 999, transition: 'width 0.5s' }} />
                </div>
                <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right', marginTop: 4 }}>
                  {Math.round(done.length / deliveries.length * 100)}% selesai
                </div>
              </>
            )}
          </div>

          {pending.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Dalam Proses</div>}
          {pending.map(d => (
            <div key={d.id} style={{ background: '#fff', border: '1px solid #e2f0e8', borderLeft: `3px solid ${d.status === 'tiba' ? '#0369a1' : '#d97706'}`, borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#14532d', marginBottom: 3 }}>{d.donorName}</div>
              <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
                <MapPin size={11} /> {d.address?.substring(0, 50) || 'Alamat tidak tersedia'}
              </div>
              <div style={{ marginTop: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: d.status === 'tiba' ? '#dbeafe' : '#fef3c7', color: d.status === 'tiba' ? '#1e40af' : '#d97706' }}>
                  {d.status === 'tiba' ? '📍 Sudah Tiba' : '🚚 Dalam Perjalanan'}
                </span>
              </div>
            </div>
          ))}

          {done.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 4 }}>✅ Selesai ({done.length})</div>
              {done.map(d => (
                <div key={d.id} style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.85 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#166534' }}>{d.donorName}</div>
                    <div style={{ fontSize: 11, color: '#4ade80' }}>{d.address?.substring(0, 40) || '-'}</div>
                  </div>
                  <CheckCircle size={16} color="#16a34a" />
                </div>
              ))}
            </>
          )}

          {deliveries.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              <Package size={28} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.3 }} />
              <div style={{ fontSize: 13 }}>Belum ada tujuan pengantaran</div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }
        @media (max-width: 768px) {
          div[style*="grid-template-columns: 1fr 300px"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
